/**
 * Custom HTTP headers editor for tool forms.
 *
 * Headers come exclusively from the bot's session variables: "Add header"
 * lists the defined variables, picking one sends it as a header (header name
 * = variable name, value resolved from the session at request time). Rows are
 * read-only — values are edited in the Variables section, not here.
 * The Authorization header is managed separately by the Bearer token field in
 * each form — see headersTemplate.ts for the split/merge helpers.
 */

import React from 'react'
import { Button } from '../../ui/Button'
import { Label } from '../../ui/label'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../ui/dropdown-menu'
import { Plus, X, ChevronDown, Braces } from 'lucide-react'
import type { HeaderRow } from './headersTemplate'

interface CustomHeadersEditorProps {
  headers: HeaderRow[]
  onChange: (headers: HeaderRow[]) => void
  /** Chatbot session variables — the only allowed source of custom headers. */
  variables?: Record<string, unknown>
}

export const CustomHeadersEditor: React.FC<CustomHeadersEditorProps> = ({ headers, onChange, variables }) => {
  const usedKeys = new Set(headers.map(h => h.key))
  const availableVariables = Object.keys(variables || {}).filter(v => !usedKeys.has(v))

  const addFromVariable = (varName: string) => {
    onChange([...headers, { key: varName, value: `{{${varName}}}` }])
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Custom Headers</Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={availableVariables.length === 0}
              className="text-muted-foreground hover:text-foreground"
            >
              <Plus className="w-4 h-4 mr-1" /> Add header <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <p className="px-2 py-1.5 text-xs text-muted-foreground">Send a variable as a header</p>
            {availableVariables.map(vName => (
              <DropdownMenuItem key={vName} onSelect={() => addFromVariable(vName)} className="font-mono cursor-pointer">
                <Braces className="w-4 h-4 opacity-60" /> {vName}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {headers.length === 0 ? (
        <div className="rounded-lg border border-dashed border-input px-3 py-2.5">
          <p className="text-xs text-muted-foreground">
            {Object.keys(variables || {}).length === 0
              ? 'No variables defined. Add variables to the bot first, then send them as headers here.'
              : 'No custom headers. Pick a variable to send it as a header with every API call.'}
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {headers.map((row, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-input bg-muted/30 px-3 py-2">
              <Braces className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <span className="font-mono text-sm">{row.key}</span>
              <span className="text-muted-foreground text-sm">:</span>
              <span className="font-mono text-xs text-muted-foreground truncate flex-1">{row.value}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(headers.filter((_, idx) => idx !== i))}
                aria-label="Remove header"
                className="shrink-0 px-2 -mr-1 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        Header values come from the bot's variables — edit them in the Variables section.
      </p>
    </div>
  )
}
